import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { DETECTIVE_TOUR_STEPS } from './detectiveTour';
import DetectiveTourOverlay from './DetectiveTourOverlay';
import { useDetectiveTourStore } from '@/store/detectiveTourStore';
import { useAskAiStore } from '@/store/askAiStore';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

/**
 * Drives the guided tour: navigation on step change, presenter keyboard
 * shortcuts, and the narration card.
 *
 * MOUNTED ABOVE THE ROUTER `<Outlet/>` (in `DashboardLayout`) so a step's
 * `navigate()` swaps the page underneath the card without unmounting the card
 * itself. Mounting this inside a page would tear the tour down on its own
 * first step.
 */
export default function DetectiveTourProvider(): JSX.Element {
  const navigate = useNavigate();
  const active = useDetectiveTourStore((s) => s.active);
  const stepIndex = useDetectiveTourStore((s) => s.stepIndex);
  const next = useDetectiveTourStore((s) => s.next);
  const back = useDetectiveTourStore((s) => s.back);
  const exit = useDetectiveTourStore((s) => s.exit);
  const start = useDetectiveTourStore((s) => s.start);

  // Navigate exactly once per (active, step) pair. Without this latch an
  // unrelated re-render would re-issue navigate() and reset any filter the
  // presenter touched by hand while narrating.
  const lastNavRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active) {
      lastNavRef.current = null;
      return;
    }
    const key = `${stepIndex}`;
    if (lastNavRef.current === key) return;
    lastNavRef.current = key;

    const route = DETECTIVE_TOUR_STEPS[stepIndex]?.route;
    // A null route means "narrate over whatever is already on screen".
    if (route) navigate(route);
  }, [active, stepIndex, navigate]);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      // The Ask AI sheet is a modal Radix dialog: its own Esc-to-close still
      // bubbles to this window listener, so without this guard dismissing the
      // panel would also end the tour mid-demo.
      if (useAskAiStore.getState().isOpen) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        exit();
      } else if (e.key === 'Home') {
        // Back to beat one without leaving the tour — the recovery key when a
        // demo question drags the narrative off course.
        e.preventDefault();
        start();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, next, back, exit, start]);

  return <DetectiveTourOverlay />;
}
